import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  getAllowedStatusValuesForTask,
  getStatusLabel,
} from "@/constants/requestConstants";
import { RequestStatus } from "@/types/request";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { ChevronDownIcon } from "lucide-react";

export const RequestStatusSelect: React.FC<{
  currentStatus: string;
  requestId: string;
  onStatusChange: (id: string, status: string) => void;
  hideCurrentStatus?: boolean;
  className?: string;
  iconClassName?: string;
  applyBadge?: boolean;
  showIcon?: boolean;
}> = ({
  currentStatus,
  requestId,
  onStatusChange,
  hideCurrentStatus,
  className,
  iconClassName,
  applyBadge = false,
  showIcon = true,
}) => {
  const { t } = useTranslation();
  const translatedStatusLabels: Record<RequestStatus, string> = {
    [RequestStatus.NEW_TASK]: t("requests.status.newTask"),
    [RequestStatus.APPROVED]: t("requests.status.approved"),
    [RequestStatus.TO_DO]: t("requests.status.toDo"),
    [RequestStatus.IN_PROGRESS]: t("requests.status.inProgress"),
    [RequestStatus.ON_HOLD]: t("requests.status.onHold"),
    [RequestStatus.COMPLETED]: t("requests.status.completed"),
    [RequestStatus.DONE]: t("requests.status.done"),
    [RequestStatus.CANCELED]: t("requests.status.cancelled"),
  };
  const allowedStatuses = getAllowedStatusValuesForTask(currentStatus).map(
    (value) => ({
      value,
      label: translatedStatusLabels[value],
    }),
  );
  const currentStatusLabel = getStatusLabel(currentStatus as RequestStatus);

  const shownLabel = hideCurrentStatus
    ? t("requests.status.changeOfStatus")
    : currentStatusLabel;

  return (
    <Select
      value={currentStatus}
      onValueChange={(newStatus) => {
        onStatusChange(requestId, newStatus);
      }}
    >
      <SelectTrigger
        className={cn("w-[120px] h-8", className)}
        iconClassName={iconClassName}
        showIcon={showIcon}
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {applyBadge ? (
          <Badge variant="outline">
            {currentStatusLabel} <ChevronDownIcon className="size-4" />
          </Badge>
        ) : (
          shownLabel
        )}
      </SelectTrigger>
      <SelectContent
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <SelectItem value={currentStatus} disabled>
          {t("requests.status.changeOfStatus")}
        </SelectItem>
        {allowedStatuses.map((statusOption) => (
          <SelectItem key={statusOption.value} value={statusOption.value}>
            {statusOption.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
